/**
 * SurfaceDismissContext — the dismissal contract a surface's chrome publishes
 * to everything rendered inside it.
 *
 * A surface (slideout, modal, side panel) owns its exits: the close and back
 * affordances render in every state the content can be in, and the *functions*
 * behind them are reachable from any depth without threading `onClose` /
 * `onBack` through props. `SlideoutMenu` provides this in both its modal and
 * non-modal paths; inline hosts that frame the same content without a
 * `SlideoutMenu` provide it themselves.
 *
 * The context is OPTIONAL by design: the same header component must render
 * inside a portaled slideout, an inline split-view pane, and a review panel
 * with no chrome at all. `useSurfaceDismiss()` returning `undefined` means "no
 * chrome here", not an error — a consumer with an explicit `onClose` prop keeps
 * using it, and one without simply renders no close.
 *
 * `close` is the chrome's own close — already routed through whatever
 * unsaved-changes guard the host installed via `onOpenChange`.
 *
 * See docs/claude/REACT_19_PATTERNS.md § 1.4.
 */

import { createContext, useContext } from 'react';

export interface SurfaceDismissContextValue {
  /** Dismiss the surface. Guarded by the host's `onOpenChange`. */
  close: () => void;
  /** Go back one step in the host's own history. `undefined` when there is nowhere to go. */
  back?: () => void;
  isEntering: boolean;
  isExiting: boolean;
}

export const SurfaceDismissContext = createContext<
  SurfaceDismissContextValue | undefined
>(undefined);
SurfaceDismissContext.displayName = 'SurfaceDismissContext';

/**
 * Read the enclosing surface's dismissal contract, or `undefined` outside any
 * surface chrome.
 */
export function useSurfaceDismiss(): SurfaceDismissContextValue | undefined {
  return useContext(SurfaceDismissContext);
}

/**
 * Marks the control that dismisses a surface. The chrome's own close button
 * carries it; a consumer that renders its own dismiss control instead must
 * carry it too, or the development-mode assertion in `SlideoutMenu` warns that
 * the open surface has no way out. Registered in frontend/e2e/HANDLES.md.
 */
export const SURFACE_DISMISS_ATTR = 'data-slideout-dismiss';
